import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Clapperboard, Film, Music2, Crown, ArrowRight } from "lucide-react";
import { Layout } from "@/components/hud/Layout";

// Content-only guide. Visual structure (background, title, intro, single glass panel, 4 sections, icons) is intentionally preserved.
const GUIDES = [
  { id: "create", label: "nav.create", Icon: Clapperboard, to: "/create", steps: 5, note: false },
  { id: "montage", label: "nav.montage", Icon: Film, to: "/montage", steps: 5, note: false },
  { id: "music", label: "nav.music", Icon: Music2, to: "/music", steps: 5, note: true },
] as const;

const PREMIUM_POINTS = [1, 2, 3] as const;

function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <li className="flex gap-4">
      <span aria-hidden className="mt-0.5 shrink-0 font-display text-sm font-bold tabular-nums tracking-wider text-[#7FD4FF]">
        {String(n).padStart(2, "0")}
      </span>
      <div className="space-y-1">
        <p className="font-display text-xs font-semibold uppercase tracking-wide text-foreground/90">{title}</p>
        <p className="text-sm leading-relaxed text-foreground/60">{body}</p>
      </div>
    </li>
  );
}

export default function Tutorial() {
  const { t } = useTranslation();
  return (
    <Layout>
      <main data-testid="tutorial-page" className="relative mx-auto max-w-3xl px-6 pb-32 pt-14 sm:px-10">
        <span className="inline-flex items-center gap-3 font-display text-xs font-semibold uppercase tracking-[0.34em] text-[#7FD4FF]">
          <span className="h-px w-8 bg-gradient-to-r from-[#2E7BFF] to-transparent" />
          {t("tutorial.eyebrow")}
        </span>
        <h1 data-testid="tutorial-title" className="mt-5 font-display text-4xl font-bold uppercase leading-[1] tracking-wide text-foreground sm:text-5xl">
          {t("tutorial.title")}
        </h1>
        <p className="mt-6 text-sm leading-relaxed text-muted-foreground sm:text-base">{t("tutorial.intro")}</p>

        <div className="glass-panel mt-10 space-y-12 rounded-2xl p-6 sm:p-9">
          {GUIDES.map(({ id, label, Icon, to, steps, note }) => (
            <section key={id} data-testid={`tutorial-section-${id}`}>
              <h2 className="inline-flex items-center gap-2 font-display text-base font-bold uppercase tracking-wide text-[#7FD4FF] md:text-lg">
                <Icon className="h-4 w-4" strokeWidth={1.75} /> {t(label)}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground/80">{t(`tutorial.${id}.desc`)}</p>
              <ol className="mt-5 space-y-5">
                {Array.from({ length: steps }, (_, i) => i + 1).map((n) => (
                  <Step key={n} n={n} title={t(`tutorial.${id}.s${n}t`)} body={t(`tutorial.${id}.s${n}b`)} />
                ))}
              </ol>
              {note && (
                <p data-testid={`tutorial-${id}-note`} className="mt-5 rounded-lg border border-[#2E7BFF]/25 bg-[#05070D]/50 px-4 py-3 text-xs leading-relaxed text-[#7FD4FF]/90">
                  {t(`tutorial.${id}.note`)}
                </p>
              )}
              <Link
                to={to}
                data-testid={`tutorial-${id}-link`}
                className="mt-5 inline-flex items-center gap-1.5 font-display text-xs font-semibold uppercase tracking-wide text-[#7FD4FF] transition-opacity hover:opacity-70"
              >
                {t(`tutorial.${id}.link`)} <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </Link>
            </section>
          ))}

          <section data-testid="tutorial-section-premium">
            <h2 className="inline-flex items-center gap-2 font-display text-base font-bold uppercase tracking-wide text-[#7FD4FF] md:text-lg">
              <Crown className="h-4 w-4" strokeWidth={1.75} /> {t("nav.premium")}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground/80">{t("tutorial.premium.desc")}</p>
            <ol className="mt-5 space-y-5">
              {PREMIUM_POINTS.map((n) => (
                <Step key={n} n={n} title={t(`tutorial.premium.p${n}t`)} body={t(`tutorial.premium.p${n}b`)} />
              ))}
            </ol>
            <Link
              to="/billing#plans"
              data-testid="tutorial-premium-link"
              className="mt-5 inline-flex items-center gap-1.5 font-display text-xs font-semibold uppercase tracking-wide text-[#7FD4FF] transition-opacity hover:opacity-70"
            >
              {t("tutorial.premium.cta")} <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
          </section>
        </div>
      </main>
    </Layout>
  );
}
